import React, { useEffect, useRef, useState } from "react";
import { Loader2, RotateCw } from "lucide-react";

/**
 * Lightweight Pannellum-based 360° viewer.
 * Loads Pannellum CSS+JS from CDN on first mount (lazy). No npm dep.
 * Supports touch/swipe (Pannellum built-in), pinch zoom, and auto-rotate.
 *
 * Usage:
 *   <Pano360Viewer src="https://.../pano1.jpg" />
 */
const CDN_CSS = "https://cdn.jsdelivr.net/npm/pannellum@2.5.6/build/pannellum.css";
const CDN_JS = "https://cdn.jsdelivr.net/npm/pannellum@2.5.6/build/pannellum.js";

let pannellumLoadPromise = null;
export function loadPannellum() {
  if (typeof window === "undefined") return Promise.resolve();
  if (window.pannellum) return Promise.resolve();
  if (pannellumLoadPromise) return pannellumLoadPromise;
  pannellumLoadPromise = new Promise((resolve, reject) => {
    // CSS
    if (!document.querySelector(`link[href="${CDN_CSS}"]`)) {
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = CDN_CSS;
      document.head.appendChild(link);
    }
    // JS
    const script = document.createElement("script");
    script.src = CDN_JS;
    script.async = true;
    script.onload = resolve;
    script.onerror = () => reject(new Error("Failed to load Pannellum from CDN"));
    document.head.appendChild(script);
  });
  return pannellumLoadPromise;
}

export default function Pano360Viewer({ src, autoRotate = true, className = "", testId = "pano-viewer" }) {
  const containerRef = useRef(null);
  const viewerRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!src || !containerRef.current) return;
    let cancelled = false;
    setLoading(true);
    setErr(null);

    loadPannellum()
      .then(() => {
        if (cancelled || !containerRef.current || !window.pannellum) return;
        // Destroy any prior viewer
        try { viewerRef.current?.destroy?.(); } catch (_e) { /* noop */ }
        viewerRef.current = window.pannellum.viewer(containerRef.current, {
          type: "equirectangular",
          panorama: src,
          autoLoad: true,
          autoRotate: autoRotate ? -2 : 0,
          autoRotateInactivityDelay: 3000,
          compass: false,
          showZoomCtrl: true,
          showFullscreenCtrl: true,
          mouseZoom: true,
          touchPanSpeedCoeffFactor: 1,
          hfov: 100,
          minHfov: 50,
          maxHfov: 120,
        });
        viewerRef.current.on("load", () => { if (!cancelled) setLoading(false); });
        viewerRef.current.on("error", (e) => { if (!cancelled) { setErr(String(e)); setLoading(false); } });
      })
      .catch((e) => { if (!cancelled) { setErr(e.message); setLoading(false); } });

    return () => {
      cancelled = true;
      try { viewerRef.current?.destroy?.(); } catch (_e) { /* noop */ }
      viewerRef.current = null;
    };
  }, [src, autoRotate]);

  return (
    <div
      className={`relative bg-black rounded-lg overflow-hidden ${className}`}
      data-testid={testId}
      // Forces its own GPU compositing layer -- WebKit/mobile Safari has a
      // known class of bugs where a WebGL canvas nested inside a rounded +
      // overflow:hidden ancestor paints black/blank until something forces a
      // repaint. translateZ(0) is the standard workaround (same fix used for
      // WebKit canvas/video-in-rounded-corner glitches generally).
      style={{ WebkitTransform: "translateZ(0)", transform: "translateZ(0)", WebkitBackfaceVisibility: "hidden" }}
    >
      <div
        ref={containerRef}
        className="w-full h-full"
        // Without this, iOS/Android intercept a one-finger drag as page
        // scroll before Pannellum's own touch handler sees it -- the
        // viewer looks unresponsive/frozen even though it loaded fine.
        style={{ touchAction: "none" }}
      />
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/60 pointer-events-none">
          <div className="text-center text-white">
            <Loader2 className="w-8 h-8 animate-spin mx-auto" />
            <div className="text-xs mt-2 font-medium opacity-80">Loading 360° view...</div>
          </div>
        </div>
      )}
      {err && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/80 text-white text-sm p-4 text-center">
          <div>
            <div className="font-bold mb-1">Couldn&apos;t load 360° image</div>
            <div className="text-xs opacity-70 break-all">{err}</div>
          </div>
        </div>
      )}
      {!loading && !err && (
        <div className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-black/60 backdrop-blur-sm text-white text-[11px] font-bold flex items-center gap-1.5 pointer-events-none">
          <RotateCw className="w-3 h-3" /> 360° · drag to look around
        </div>
      )}
    </div>
  );
}
